/* eslint-disable max-lines */
import type { ElementRef, SimpleChanges } from '@angular/core';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
  inject,
  input,
} from '@angular/core';
import type {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { select, Store } from '@ngrx/store';
import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { FullCandidateDataService } from '@core/candidate-data';
import { CandidatePersonalDetailsFormComponent } from '@core/candidate-smart-ui';
import {
  Candidate,
  CandidateCustomField,
  CandidateDetailsForm,
  FullCandidate,
} from '@core/candidate-types';
import { CandidateWorkLocationsButtonComponent } from '@core/candidate-ui';
import { fileToBase64, noWhitespaceValidator } from '@core/common-legacy';
import { CoverLetterType } from '@core/cover-letter-types';
import { CoverLetterFileComponent, CoverLetterTypeComponent } from '@core/cover-letter-ui';
import type { Attribute } from '@core/http-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { Location } from '@core/locations-types';
import type { MailboxConvertData } from '@core/mailbox-types';
import { JobsWrapperActionsService, JobsWrapperDataService } from '@core/offers-data';
import type { JobItem, OfferKind } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { SourcesActions, TagsActions } from '@core/tags-and-sources-data';
import {
  CandidateSourcesTypeaheadWrapperComponent,
  CandidateTagsTypeaheadWrapperComponent,
} from '@core/tags-and-sources-legacy';
import { TalentPoolsDataService } from '@core/talent-pools-data';
import type { TalentPool, TalentPoolBrief } from '@core/talent-pools-types';
import type { FileReadResult, IconName } from '@core/user-interface-legacy';
import {
  AvatarComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogService,
  DividerComponent,
  FileHandlerComponent,
  FileReadStatus,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  MessageComponent,
  NotificationsController,
  ProtectedControlDirective,
  RtTooltip,
  TextareaComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { AssignOffersNewCandidateFacade } from '../../facades/assign-offers-new-candidate.facade';
import type { Assignment } from '../../facades/assign-offers.facade';
import { EditWorkLocationsFacade } from '../../facades/edit-work-locations-facade.service';
import { AddCandidateInformationEditorComponent } from '../add-candidate-information-editor/add-candidate-information-editor.component';
import { CoverLetterFileStoreService } from '../cover-letter-file-store.service';

export enum validationFields {
  emails = 'emails',
}

type serverValidation = {
  [validationFields.emails]: string;
};

export interface AddCandidateFinalValue {
  attachment: any;
  coverLetter: any;
  coverLetterFile: string | null;
  customFields: CandidateCustomField[];
  cv: any;
  emails: string[];
  links: string[];
  mailboxThreadId: null | number;
  name: string;
  jobs: Array<{ id: number; locationIds: number[] | null }>;
  talentPools: number[];
  phones: string[];
  photo: File | null;
  photoUrl: string | null;
  socialLinks: string[];
  sources: Attribute[];
  tags: Attribute[];
}

export function storeServiceFactory(
  storeService = inject(CoverLetterFileStoreService, { skipSelf: true, optional: true }),
) {
  return storeService ? storeService : new CoverLetterFileStoreService();
}

type CandidateJob = {
  id: number;
  title: string;
  locations: Location[];
  locationIds: number[];
  kind: OfferKind.Job;
  status: string;
  locationsEditable: boolean;
};

type CandidateTalentPool = {
  id: number;
  title: string;
  kind: OfferKind.TalentPool;
  status: string;
};

@Component({
  selector: 'rt-add-candidate-form',
  templateUrl: './add-candidate-form.component.html',
  styleUrls: ['./add-candidate-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CoverLetterFileStoreService,
      useFactory: storeServiceFactory,
    },
    AssignOffersNewCandidateFacade,
  ],
  imports: [
    DialogHeaderComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    DialogBodyComponent,
    ReactiveFormsModule,
    AvatarComponent,
    GroupComponent,
    IconButtonComponent,
    RtTooltip,
    ProtectedControlDirective,
    HeadingComponent,
    OfferStatusIndicatorComponent,
    CandidateWorkLocationsButtonComponent,
    DividerComponent,
    MessageComponent,
    AddCandidateInformationEditorComponent,
    TruncateComponent,
    FileHandlerComponent,
    CoverLetterTypeComponent,
    CoverLetterFileComponent,
    TextareaComponent,
    DialogFooterComponent,
    TranslatePipe,
    CandidateTagsTypeaheadWrapperComponent,
    CandidateSourcesTypeaheadWrapperComponent,
    CandidatePersonalDetailsFormComponent,
    CandidatePersonalDetailsFormComponent,
  ],
})
export class AddCandidateFormComponent {
  private readonly jobsWrapperActionsService = inject(JobsWrapperActionsService);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly notificationCtrl = inject(NotificationsController);
  private readonly store = inject(Store);
  private readonly fullCandidateDataService = inject(FullCandidateDataService);
  private readonly talentPoolsData = inject(TalentPoolsDataService);
  private readonly assignOffersFacade = inject(AssignOffersNewCandidateFacade);
  private readonly editPreferredLocationsFacade = inject(EditWorkLocationsFacade);
  protected readonly cdRef = inject(ChangeDetectorRef);
  public readonly clFileStore = inject(CoverLetterFileStoreService);

  public readonly candidate = input<any>();
  public readonly headerButton = input<boolean>(false);
  public readonly headerText = input<string>(undefined!);
  public readonly buttonText = input<string>(undefined!);
  public readonly endpoint = input<(arg: MailboxConvertData) => Observable<Candidate>>(
    this.fullCandidateDataService.save.bind(this.fullCandidateDataService),
  );
  public readonly onlyForm = input<boolean>(false);
  public readonly disallowCvUpdate = input<boolean>(false);
  public readonly avatarInitial = input<string>();
  public readonly focusOnCoverLetter = input(true, { transform: booleanAttribute });
  @Input() public loading: boolean;
  @Input() public buttonIcon: IconName;
  @Input() public photoUrl: string | null;
  @Input() public hasAvatarImage?: boolean;
  @Input() public warningMessage: string;

  public cancelClick = output<void>();
  public submitClick = output<any>();
  public editPreferredLocations = output<{
    offerId: number;
    locationIds: number[];
  }>();
  public valueChange = output<AddCandidateFinalValue>();

  @ViewChild('photoFileInput', { static: true }) public photoFileInput: ElementRef;
  @ViewChild('candidateInfo', { static: true })
  public candidateInfo: AddCandidateInformationEditorComponent;

  @ViewChild('coverLetterFileHandler', { read: FileHandlerComponent })
  public coverLetterFileHandler: FileHandlerComponent;

  @ViewChild('cvFileHandler', { read: FileHandlerComponent })
  public cvFileHandler: FileHandlerComponent;

  public candidateJobs$: BehaviorSubject<CandidateJob[]> = new BehaviorSubject([]);
  public candidateTalentPools$: BehaviorSubject<CandidateTalentPool[]> = new BehaviorSubject([]);
  public candidateForm: UntypedFormGroup;
  public coverLetterFileName: string | null;
  public cvFileName: string | null;

  public candidateInformation: {
    emails: string[];
    phones: string[];
    links: string[];
    socialLinks: string[];
    customFields: CandidateCustomField[];
  } = {
    emails: [],
    phones: [],
    links: [],
    socialLinks: [],
    customFields: [],
  };

  public abilities = {
    manageAttributes: false,
    viewAllJobs: false,
  };

  public coverLetterType: CoverLetterType | null = null;
  public CoverLetterType = CoverLetterType;
  public candidateJobs: CandidateJob[] = [];
  public candidateTalentPools: CandidateTalentPool[] = [];
  public showAllFields = false;
  public visibleFields: string[] = ['emails', 'phones'];
  public validationServerErrors: serverValidation = { [validationFields.emails]: '' };
  public ReadStatus = FileReadStatus;
  public isCoverLetterSet = false;
  // We have to keep formSub because of dynamic form binding
  private candidateFormSubscription: Subscription;
  private ngOnDestroy$ = new Subject<void>();
  private offersMap: Record<number, JobItem | TalentPool> = {};

  public get disabled(): boolean {
    return this.candidateForm.invalid || this.candidateInfo.form.invalid;
  }

  public get detailsForm() {
    return <CandidateDetailsForm>this.candidateForm.controls.basic;
  }

  constructor() {
    this.jobsWrapperActionsService.fetchActiveCollection();

    combineLatest([
      this.jobsWrapperDataService.getActiveJobItemsCanAssignTo$(),
      this.talentPoolsData.collection$(),
    ])
      .pipe(
        map(data =>
          data.flat().reduce(
            (acc, offer) => {
              acc[offer.id] = offer;
              return acc;
            },
            <Record<number, JobItem | TalentPool>>{},
          ),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(offersMap => {
        this.offersMap = offersMap;
        this.candidateForm && this.updateCandidateOffers();
      });

    combineLatest([this.candidateJobs$, this.candidateTalentPools$])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([candidateJobs, candidateTalentPools]) => {
        this.candidateJobs = candidateJobs;
        this.candidateTalentPools = candidateTalentPools;
      });
  }

  public ngOnInit(): void {
    this.bindForm();
    this.updateCandidateOffers();
    this.checkAbilities();
    this.clFileStore.data$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(file => {
      this.setCoverLetterData();
      this.cdRef.detectChanges();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.candidate &&
      !changes.candidate.firstChange &&
      changes.candidate.currentValue.id !== changes.candidate.previousValue.id
    ) {
      this.bindForm();
    }
  }

  public ngOnDestroy(): void {
    if (this.candidateFormSubscription) {
      this.candidateFormSubscription.unsubscribe();
    }
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getControl(
    controlName:
      | 'photo'
      | 'photoUrl'
      | 'name'
      | 'jobs'
      | 'talentPools'
      | 'attachment'
      | 'coverLetter'
      | 'coverLetterFile'
      | 'cv'
      | 'mailboxThreadId',
  ): UntypedFormControl;
  public getControl(controlName: 'tags' | 'sources' | 'emails'): UntypedFormArray;
  public getControl(controlName: string): AbstractControl | null {
    return this.candidateForm.get('basic')!.get(controlName);
  }

  public getCandidateInformation(candidate: FullCandidate): {
    emails: string[];
    phones: string[];
    links: string[];
    socialLinks: string[];
    customFields: CandidateCustomField[];
  } {
    return {
      emails: candidate ? candidate.emails : [],
      phones: candidate ? candidate.phones : [],
      links: candidate ? candidate.links : [],
      socialLinks: candidate ? candidate.socialLinks : [],
      customFields: candidate ? candidate.customFields : [],
    };
  }

  public submit(): void {
    const hasOffers =
      !!this.getControl('jobs')!.value.length || this.getControl('talentPools')!.value.length;
    !this.abilities.viewAllJobs && !hasOffers ? this.showConfirmationDialog() : this.save();
  }

  public showConfirmationDialog(): void {
    const message = this.i18n.translate('app.candidate.confirmation_dialog.message');
    const title = this.i18n.translate('app.candidate.confirmation_dialog.title');
    const agreeLabel = this.i18n.translate('app.candidate.confirmation_dialog.agree_label');

    this.openConfirmDialog(message, title, 'warning', true, agreeLabel).subscribe(confirmed => {
      if (confirmed) this.save();
    });
  }

  public save(): void {
    const value = this.clearEmptyValues({
      ...this.candidateForm.get('basic')!.value,
      ...this.candidateInfo.form.value,
    });

    const { jobs, talentPools, tags, sources, mailboxThreadId, attachment, ...candidate } = value;

    const candidateValue = this.candidate();
    const attachmentAssignmentId =
      !candidate.cv && candidateValue.attachment ? candidateValue.attachment.id : null;
    this.loading = true;
    this.cdRef.detectChanges();
    this.endpoint()({
      data: candidate,
      talentPools: value.talentPools,
      offers: value.jobs,
      mailboxThreadId: value.mailboxThreadId,
      attachmentAssignmentId,
    }).subscribe(
      data => {
        this.candidateInfo.form.markAsPristine();
        this.candidateInfo.form.markAsUntouched();
        this.candidateForm.markAsPristine();
        this.candidateForm.markAsUntouched();

        if (value.tags.length > 0) {
          this.store.dispatch(
            TagsActions.createTag({
              payload: {
                candidateId: data.id,
                tagName: value.tags.map((t: { value: string | string[] }) => t.value),
              },
            }),
          );
        }

        if (value.sources.length > 0) {
          this.store.dispatch(
            SourcesActions.createSource({
              payload: {
                candidateId: data.id,
                sourceName: value.sources.map((t: { value: string | string[] }) => t.value),
              },
            }),
          );
        }

        this.loading = false;
        this.cdRef.detectChanges();
        this.submitClick.emit(data);
        this.notificationCtrl.success(this.i18n.translate('app.candidate.add_success'));
      },
      ({
        error,
      }: {
        error: { errorFields: { emails: string[]; name: string }; error: string[] };
      }) => {
        const emailError = error.errorFields && error.errorFields.emails;
        const message = error.error[0];
        const errorMessage = emailError && message ? message : '';

        const nameError = error.errorFields && error.errorFields.name;
        const nameMessage = nameError && message ? message : '';

        this.setValidationError(validationFields.emails, errorMessage);

        if (nameMessage) this.notificationCtrl.error(nameMessage);

        this.loading = false;
        this.cdRef.detectChanges();
      },
    );
  }

  public resolveAvatarType(): 'initials' | 'icon' | 'image' {
    if (this.photoUrl && this.hasAvatarImage) {
      return 'image';
    }
    if ((!this.photoUrl || !this.hasAvatarImage) && this.avatarInitial()) {
      return 'initials';
    }
    return 'icon';
  }

  public setValidationError(fieldName: validationFields, error: string): void {
    const newErrors = {
      ...this.validationServerErrors,
      [fieldName]: error,
    };
    this.validationServerErrors = newErrors;
    this.cdRef.detectChanges();
  }

  public addTag(attr: Attribute): void {
    const control = this.candidateForm.get(['basic', 'tags'])!;
    control.setValue([...control.value, attr]);
  }

  public addSource(attr: Attribute): void {
    const control = this.candidateForm.get(['basic', 'sources'])!;
    control.setValue([...control.value, attr]);
  }

  public deleteTag(attr: Attribute): void {
    const control = this.candidateForm.get(['basic', 'tags'])!;
    control.setValue(control.value.filter((t: Attribute) => t.value !== attr.value));
  }

  public deleteSource(attr: Attribute): void {
    const control = this.candidateForm.get(['basic', 'sources'])!;
    control.setValue(control.value.filter((t: Attribute) => t.value !== attr.value));
  }

  public cvChanged(result: FileReadResult): void {
    if (result?.status !== FileReadStatus.OK) {
      return;
    }

    const base64$: Observable<string | null> = result.files
      ? fileToBase64(<File>result.files)
      : of(null);

    base64$.pipe(take(1)).subscribe(base64 => {
      this.getControl('attachment').setValue(null);
      this.getControl('cv').setValue(base64);
      this.cvFileName = (<File | null>result.files)?.name || null;
      this.cdRef.detectChanges();
    });
  }

  public photoChanged(files: FileList | null): void {
    const file = files?.[0] || null;
    const photoUrlControl = this.getControl('photoUrl');
    // @ts-ignore
    const hasFileReader = window['FileReader'];

    if (file && hasFileReader && file.type.indexOf('image') > -1) {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = (e: any) => {
        this.photoUrl = e.target.result;
        this.hasAvatarImage = true;
        this.getControl('photo').setValue(e.target.result);
        photoUrlControl.setValue(this.photoUrl);
        this.cdRef.detectChanges();
      };
    } else {
      this.hasAvatarImage = false;
      this.photoUrl = null;
      this.getControl('photo').setValue(null);
      photoUrlControl.setValue(this.photoUrl);
      this.cdRef.detectChanges();
    }
    this.photoFileInput.nativeElement.value = null;
  }

  public openOffersAssignments(): void {
    this.assignOffersFacade.execute({
      jobAssignments: this.candidateJobs,
      talentPoolAssignments: this.candidateTalentPools,
      options: {
        onSubmit: (
          jobsToDelete: Assignment[],
          jobsToUpdate: Assignment[],
          talentPoolsToDelete: Assignment[],
          talentPoolsToUpdate: Assignment[],
        ) => {
          [...jobsToDelete, ...talentPoolsToDelete].forEach(({ id }) => {
            this.removeOffer(id);
          });
          const jobsControl = this.getControl('jobs');
          jobsControl.setValue([...jobsControl.value, ...jobsToUpdate]);

          const talentPoolControl = this.getControl('talentPools');
          talentPoolControl.setValue([
            ...talentPoolControl.value,
            ...talentPoolsToUpdate.map(({ id }) => id),
          ]);
        },
      },
      isSelectionAlwaysSaveable: true,
    });
  }

  public removeOffer(offerId: number): void {
    const jobsControl = this.getControl('jobs');
    jobsControl.setValue(jobsControl.value.filter(({ id }: { id: number }) => id !== offerId));
    const talentPoolControl = this.getControl('talentPools');
    talentPoolControl.setValue(talentPoolControl.value.filter((id: number) => id !== offerId));
  }

  public toggleVisibleFields(): void {
    const showAllFields = !this.showAllFields;
    this.showAllFields = showAllFields;
    this.visibleFields = showAllFields
      ? ['emails', 'phones', 'socialLinks', 'links', 'customFields']
      : ['emails', 'phones'];
    this.cdRef.detectChanges();
  }

  public candidateProfileInfoValueChanges(): void {
    this.setValidationError(validationFields.emails, '');
  }

  public trackByOffer(index: number, offer: { id: number }): number {
    return offer && offer.id;
  }

  public trackByItem(index: number, item: string): string {
    return item;
  }

  public onCoverLetterTypeSelected(coverLetterType: CoverLetterType): void {
    this.coverLetterType = coverLetterType;

    if (coverLetterType === CoverLetterType.FILE) {
      this.coverLetterFileHandler.instance.readFileFromDisk();
    } else {
      this.isCoverLetterSet = true;
      this.cdRef.detectChanges();
    }
  }

  public onCoverLetterTextClose(): void {
    this.coverLetterType = null;
    this.isCoverLetterSet = false;
    this.getControl('coverLetter').reset();
    this.cdRef.detectChanges();
  }

  public onNextCoverLetterFile($event: FileReadResult): void {
    if ($event.status === FileReadStatus.OK) {
      this.isCoverLetterSet = true;
      this.clFileStore.set(<File>$event.files, this.candidate().id);
    } else {
      this.isCoverLetterSet = false;
      this.coverLetterType = null;
    }
  }

  public onCoverLetterFileRemoved(): void {
    this.clFileStore.remove(this.candidate().id);
    this.coverLetterType = null;
    this.isCoverLetterSet = false;
    this.cdRef.detectChanges();
  }

  public onCoverLetterFileChange(): void {
    this.coverLetterFileHandler.instance.readFileFromDisk();
  }

  public editLocations(job: CandidateJob): void {
    this.editPreferredLocationsFacade
      .editLocations$(job.id, job.locations?.map(location => location.id) || [])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(locationIds => {
        const control = this.getControl('jobs');
        control.setValue([
          ...control.value.filter((o: { id: number; locationIds: number[] }) => o.id !== job.id),
          { id: job.id, locationIds },
        ]);
        this.editPreferredLocations.emit({ offerId: job.id, locationIds });
      });
  }

  private candidateAttr(name: string, def: any): any {
    if (name === 'attachment') {
      const candidate = this.candidate();
      return (candidate && candidate.attachment && candidate.attachment.attachment) || def;
    }
    const candidate = this.candidate();
    return (candidate && candidate[name]) || def;
  }

  private clearEmptyValues(candidate: FullCandidate): any {
    const candidateCopy = { ...candidate };
    Object.entries(candidateCopy).forEach(([key, values]) => {
      // @ts-ignore
      candidateCopy[key] = Array.isArray(values) ? (values || []).filter(Boolean) : values;
    });
    candidateCopy.customFields = candidateCopy.customFields.filter(
      (customField: CandidateCustomField) => customField['label']?.length,
    );
    return candidateCopy;
  }

  private bindForm(): void {
    const candidate = this.candidate();
    const candidateValue = this.candidate();
    this.candidateForm = this.formBuilder.group({
      info: this.candidateInfo.form,
      basic: this.formBuilder.group({
        photo: this.formBuilder.control(null),
        photoUrl: this.formBuilder.control(this.photoUrl),
        coverLetterFile: this.formBuilder.control(null),
        name: this.formBuilder.control(
          this.candidateAttr('name', ''),
          Validators.compose([Validators.required, noWhitespaceValidator]),
        ),
        salutation: this.formBuilder.control(this.candidateAttr('salutation', null)),
        title: this.formBuilder.control(this.candidateAttr('title', null)),
        jobs: this.formBuilder.control(
          this.candidateAttr(
            'offers',
            candidate.offerId ? [{ id: candidate.offerId, locationIds: [] }] : [],
          ),
        ),
        talentPools: this.formBuilder.control(
          this.candidateAttr(
            'talentPools',
            candidateValue.talentPoolId ? [candidateValue.talentPoolId] : [],
          ),
        ),
        tags: this.formBuilder.control(this.candidateAttr('tags', [])),
        sources: this.formBuilder.control(this.candidateAttr('sources', [])),
        cv: this.formBuilder.control(null),
        attachment: this.formBuilder.control(this.candidateAttr('attachment', null)),
        coverLetter: this.formBuilder.control(this.candidateAttr('coverLetter', null)),
        mailboxThreadId: this.formBuilder.control(this.candidateAttr('mailboxThreadId', null)),
      }),
    });

    this.candidateInformation = this.getCandidateInformation(this.candidate());

    if (this.candidateFormSubscription) {
      this.candidateFormSubscription.unsubscribe();
    }
    this.candidateFormSubscription = this.candidateForm.valueChanges.subscribe(() => {
      this.updateCandidateOffers();
      const finalValue: AddCandidateFinalValue = {
        ...this.candidateForm.get('basic')!.value,
        ...this.candidateInfo.form.value,
      };
      this.valueChange.emit(finalValue);
      this.cdRef.detectChanges();
    });
    this.setInitialCoverLetterFileData();
  }

  private setInitialCoverLetterFileData(): void {
    this.setCoverLetterData();
    this.coverLetterType = this.clFileStore.get(this.candidate().id)
      ? CoverLetterType.FILE
      : this.getControl('coverLetter').value?.length
        ? CoverLetterType.TEXT
        : null;
    this.isCoverLetterSet = this.coverLetterType !== null;
    this.cdRef.detectChanges();
  }

  private setCoverLetterData(): void {
    const coverLetterFile = this.clFileStore.get(this.candidate().id);
    this.getControl('coverLetterFile').setValue(coverLetterFile?.base64);
    this.coverLetterFileName = coverLetterFile?.name || null;
  }

  private updateCandidateOffers(): void {
    const candidateJobs = this.getControl('jobs').value.map(
      (offer: { id: number; locationIds: number[] | null }) => {
        const job = <JobItem>this.offersMap[offer.id];

        return {
          id: job.id,
          title: job.title,
          locationIds: job.locationIds || [],
          locations: offer.locationIds?.map(locationId =>
            job.locations?.find(location => location.id === locationId),
          ),
          kind: job.kind,
          status: job.status,
          locationsEditable:
            (job.locations || []).filter(location => !location.archivedAt).length > 1,
        };
      },
    );
    this.candidateJobs$.next(candidateJobs);

    const candidateTalentPools = this.getControl('talentPools').value.map((id: number) => {
      const talentPool = <TalentPoolBrief>this.offersMap[id];

      return {
        id: talentPool.id,
        title: talentPool.title,
        kind: talentPool.kind,
        status: talentPool.status,
      };
    });
    this.candidateTalentPools$.next(candidateTalentPools);
  }

  private openConfirmDialog(
    message: string,
    title: string,
    variant: string,
    safeFooter: boolean,
    agreeLabel: string,
  ): Observable<boolean> {
    return this.dialogService.confirm({
      title,
      message,
      variant,
      isCancelable: true,
      safeFooter,
      agreeLabel,
    });
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          viewAllJobs: enabledMap[Ability.ViewAllJobs],
          manageAttributes: enabledMap[Ability.ManageAttributes],
        };
        this.cdRef.detectChanges();
      });
  }
}
